import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getConsent, acceptAll, rejectAll } from '../utils/consentManager';

const CookieConsentBanner = () => {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const consent = getConsent();
    if (consent === null) {
      // Show banner after a short delay for better UX
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    }

    // Listen for reset event
    const handleReset = () => {
      setShowBanner(true);
    };

    window.addEventListener('cookieConsentReset', handleReset);
    return () => window.removeEventListener('cookieConsentReset', handleReset);
  }, []);

  const handleAccept = () => {
    acceptAll();
    setShowBanner(false);
  };

  const handleReject = () => {
    rejectAll();
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6 animate-slideUp">
      <div className="mx-auto max-w-5xl">
        <div className="relative overflow-hidden rounded-2xl border border-purple-500/20 bg-gradient-to-br from-slate-900/95 via-slate-900/95 to-purple-950/95 backdrop-blur-xl shadow-2xl">
          {/* Ambient glow effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-purple-500/5 via-pink-500/5 to-cyan-500/5" />

          <div className="relative px-6 py-5 md:px-8 md:py-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex-1">
                <div className="flex items-start gap-3">
                  {/* Cookie icon */}
                  <div className="flex-shrink-0">
                    <svg
                      className="h-6 w-6 text-purple-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
                      />
                    </svg>
                  </div>

                  <div className="flex-1 space-y-1.5">
                    <h3 className="text-base font-semibold text-white">
                      We value your privacy
                    </h3>
                    <p className="text-sm leading-relaxed text-white/70">
                      We use cookies and analytics to understand how you use Nysa Learning Lab and improve your
                      learning experience. All your progress data stays private and secure.{' '}
                      <Link
                        to="/privacy-policy"
                        className="text-purple-400 underline decoration-purple-400/30 underline-offset-2 transition-colors hover:text-purple-300 hover:decoration-purple-300/50"
                      >
                        Learn more
                      </Link>
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row md:flex-shrink-0">
                <button
                  onClick={handleReject}
                  className="rounded-xl border border-white/15 bg-white/5 px-5 py-2.5 text-sm font-semibold text-white/90 transition-all hover:bg-white/10 hover:border-white/25 focus:outline-none focus:ring-2 focus:ring-purple-400/50"
                >
                  Reject
                </button>
                <Link
                  to="/privacy-settings"
                  className="rounded-xl border border-white/15 bg-white/5 px-5 py-2.5 text-sm font-semibold text-white/90 transition-all hover:bg-white/10 hover:border-white/25 focus:outline-none focus:ring-2 focus:ring-purple-400/50 text-center"
                >
                  Settings
                </Link>
                <button
                  onClick={handleAccept}
                  className="rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-purple-500/30 transition-all hover:shadow-purple-500/40 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-purple-400/50"
                >
                  Accept All
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
